import { useState, useCallback } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import logo from "@/assets/logo-white-small.png";
import { trackPhoneClick } from "@/lib/gads";
import { Button } from "@/components/ui/button";

const navLinks = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Products", href: "#products" },
  { label: "Services", href: "#services" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "#contact" },
  { label: "Careers", href: "/careers" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const handleHashClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      // Only intercept when already on home — let Link handle cross-page nav
      if (location.pathname === "/" && href.startsWith("#")) {
        e.preventDefault();
        const id = href.slice(1);
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        window.history.replaceState(null, "", "/" + href);
      }
      setOpen(false);
    },
    [location.pathname]
  );

  const renderLink = (l: { label: string; href: string }, className: string) => {
    const to = l.href.startsWith("#") ? "/" + l.href : l.href;
    return (
      <Link
        key={l.href}
        to={to}
        onClick={(e) => handleHashClick(e, l.href)}
        className={className}
      >
        {l.label}
      </Link>
    );
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm border-b border-primary-foreground/10" role="banner">
      <div className="container mx-auto flex items-center justify-between h-20 md:h-24 px-4 gap-4">
        <Link
          to="/#home"
          onClick={(e) => handleHashClick(e, "#home")}
          className="flex items-center gap-2 shrink-0"
        >
          <img src={logo} alt="" className="h-16 md:h-20 w-auto object-contain shrink-0" width={160} height={160} />
          <span className="font-display text-sm sm:text-base font-bold tracking-wide text-primary-foreground leading-tight">
            TAHER STEEL<br /> TUBE CO.
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
          {navLinks.map((l) =>
            renderLink(
              l,
              "text-primary-foreground/80 hover:text-accent font-body text-sm font-medium uppercase tracking-wider transition-colors"
            )
          )}
        </nav>

        <a
          href="tel:+919894105887"
          onClick={trackPhoneClick}
          className="hidden md:flex items-center gap-2 bg-accent text-accent-foreground px-4 py-2 rounded font-display font-semibold text-sm hover:brightness-110 transition"
        >
          <Phone className="w-4 h-4" />
          Call Now
        </a>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setOpen(!open)}
          className="md:hidden h-11 w-11 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {open && (
        <nav className="md:hidden bg-primary border-t border-primary-foreground/10 px-4 pb-4" aria-label="Mobile navigation">
          {navLinks.map((l) =>
            renderLink(
              l,
              "block py-3 text-primary-foreground/80 hover:text-accent font-body text-sm uppercase tracking-wider border-b border-primary-foreground/5"
            )
          )}
          <a
            href="tel:+919894105887"
            onClick={trackPhoneClick}
            className="mt-3 flex items-center justify-center gap-2 bg-accent text-accent-foreground px-4 py-3 rounded font-display font-semibold"
          >
            <Phone className="w-4 h-4" />
            Call Now
          </a>
        </nav>
      )}
    </header>
  );
};

export default Header;
